import type { ReactNode } from "react";
import { PageHeader } from "./PageHeader";
import { useAuth } from "../context/AuthContext";

type Props = {
  /** Single permission, or any-of (OR) when an array and requireAll is false. */
  permission: string | string[];
  /** When true with a permission array, user must have every listed permission (AND). */
  requireAll?: boolean;
  /** Additional permissions that must all pass (AND) after the base `permission` check. */
  alsoRequireAll?: string[];
  children: ReactNode;
};

export function RequirePagePermission({
  permission,
  requireAll = false,
  alsoRequireAll,
  children,
}: Props) {
  const { can, loading, authRequired, user } = useAuth();

  if (loading) {
    return (
      <div className="workspace workspace--empty" style={{ padding: "3rem 1.5rem", textAlign: "center" }}>
        <p className="section-lead">Loading…</p>
      </div>
    );
  }

  if (authRequired && !user) {
    return null;
  }

  const baseAllowed = Array.isArray(permission)
    ? requireAll
      ? permission.every((p) => can(p))
      : permission.some((p) => can(p))
    : can(permission);
  const extraOk =
    !alsoRequireAll?.length || alsoRequireAll.every((p) => can(p));
  const allowed = baseAllowed && extraOk;
  if (!allowed) {
    return (
      <div className="workspace">
        <PageHeader breadcrumb={[{ label: "Home", to: "/" }, { label: "Access" }]} title="No access" />
        <p className="tab-intro">Your account does not include permission for this section. Contact a superadmin.</p>
      </div>
    );
  }

  return <>{children}</>;
}
